import _ from "lodash";
import React from "react";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classes from "./MessageAttachmentCard.scss";
import classNames from "classnames";
import { AttachmentList } from "Attachment";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import I18nHOC from "UIComponents/I18nHOC";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import { getCommentDisplayContent, getStyleStrippedText } from "Utils";
import { getGlobalTimezone } from "Utils/TimezoneHelpers";
import { DoubleTick } from "SvgComponents";
import { PinFilled, PinOutlined, DeleteOutlined } from "@toddle-edu/ds-icons";
import { colors } from "Constants";
import {
  getUserIdentifierName,
  getFormattedUserName,
} from "Utils/GetFormattedUserName";

const TimeComponent = ({ time }) => {
  return <div className={classes.time}>{time}</div>;
};

const mapStateToProps = () => ({
  timezone: getGlobalTimezone(),
});

const Time = connect(mapStateToProps)(TimeComponent);

const MessageAttachmentCard = React.memo(
  ({
    item,
    message,
    isRemark,
    isOwnChat,
    createdAt,
    itemType,
    createdBy = {},
    showSenderInfo,
    showMessageTime,
    showFlatMessage,
    studentData,
    attachmentCardType,
    horizontalCardStyle,
    isRightPaneChat,
    senderNameStyle,
    messageWrapperStyle,
    showIsRead,
    isRead,
    showRemarkOption,
    togglePinMessage,
    isPinMessageLoading,
    pinMessages,
    userType,
    t,
    isPinPopup,
    isDeleted,
    messageSubConStyle,
    attachmentListContainerStyle,
    flatMsgContainerStyle,
    horizontalMessageCardV2,
    attachmentListContainerStyleV2,
    isAlertTypeMessage,
  }) => {
    const attachments = _.get(item, "attachments", []);

    const yellowMsg = [
      "ASSIGNMENT_RESOURCE",
      "STUDENT_ASSIGNMENT_SUBMISSION",
      "STUDENT_ASSIGNMENT_EVALUATED",
      "STUDENT_ASSIGNMENT_SHARED",
      "STUDENT_ASSIGNMENT_EVALUATED_SHARED",
    ];
    const redMsg = ["UNHAND_STUDENT_ASSIGNMENT_SUBMISSION"];
    const showRemarkText = showRemarkOption && isRemark;
    const { firstName, lastName, type: createdByType } = createdBy;

    const containerClass = classNames({
      [classes.message]: true,
      [classes.reviewMessage]: showRemarkText && !showFlatMessage,
      [classes.ownMessage]: isOwnChat,
      [classes.systemMessage]: _.includes(yellowMsg, itemType),
      [classes.redSystemMsg]: _.includes(redMsg, itemType),
      [classes.flatMessage]: showFlatMessage,
      [classes.pinnedFlatMessage]: showFlatMessage && showRemarkText,
      [classes.alert]: isAlertTypeMessage,
    });

    const mainContainerClasses = classNames({
      [classes.mainContainer]: true,
      [classes.alertTypeStyle]: isAlertTypeMessage,
      [classes.mainContainerFullSize]:
        attachments.length >= 2 || !!_.get(item, "voiceInstruction"),
    });

    const messageFooterClasses = classNames({
      [classes.messageFooter]: true,
      [classes.messageFooterRight]: isOwnChat,
    });

    const messageBottomTextContainer = classNames({
      [classes.messageBottomTextContainer]: true,
      [classes.messageBottomTextContainerRight]: isOwnChat,
    });

    const showPinContainer = classNames({
      [classes.showPin]: !showFlatMessage,
    });

    const pinIconContainer = classNames({
      [classes.pinIcon]: showFlatMessage,
    });

    const pinIconFilledContainer = classNames({
      [classes.pinIconFilled]: true,
      [classes.pinIconLeftMargin]: !showFlatMessage,
    });

    const getMessage = () => {
      const studentIdentifierName = getUserIdentifierName({
        user: studentData,
      });
      switch (itemType) {
        case "ASSIGNMENT_RESOURCE": {
          const assignmentTitle = _.get(item, "assignmentTitle", "");
          const label = _.get(item, "label", "");
          return `<div class=${classes.assignmentCardTitleDescCon}>${
            !_.isEmpty(assignmentTitle)
              ? `<div class=${classes.assignmentCardTitle}>${assignmentTitle}</div>`
              : ""
          }${label}</div>`;
        }
        case "STUDENT_ASSIGNMENT_SUBMISSION":
          return isOwnChat
            ? t("classRoom:work_submitted_msg")
            : t("classRoom:turned_in_submission_with_name", {
                firstName,
                lastName,
              });
        case "UNHAND_STUDENT_ASSIGNMENT_SUBMISSION":
          return isOwnChat
            ? t("classRoom:you_returned_student_submission", {
                firstName: studentIdentifierName,
              })
            : t("classRoom:teacher_returned_submission", {
                firstName,
                lastName,
              });
        case "STUDENT_ASSIGNMENT_EVALUATED":
          return isOwnChat
            ? t("classRoom:you_evaluated_student_submission", {
                firstName: studentIdentifierName,
              })
            : "";
        case "STUDENT_ASSIGNMENT_SHARED":
          return isOwnChat
            ? t("classRoom:you_shared_student_submission", {
                firstName: studentIdentifierName,
              })
            : t("classRoom:work_evaluated_msg");
        case "STUDENT_ASSIGNMENT_EVALUATED_SHARED":
          return isOwnChat
            ? t("classRoom:you_evaluated_shared_student_submission", {
                firstName: studentIdentifierName,
              })
            : t("classRoom:work_evaluated_msg");
        default:
          return message;
      }
    };

    let mainContainerStyle, attachmentListConStyle;

    if (!_.isEmpty(attachments) && !isDeleted) {
      const lengthOfAttachments = _.get(attachments, "length", 0);
      switch (lengthOfAttachments) {
        case 1: {
          attachmentListConStyle = {
            gridTemplateColumns: "repeat(1,1fr)",
          };
          const singleAttachmentType = _.get(_.first(attachments), "type", "");
          if (singleAttachmentType == "VIDEO") {
            mainContainerStyle = {
              width: "100%",
            };
          } else {
            mainContainerStyle = {
              maxWidth: "50%",
              width: "100%",
            };
          }
          break;
        }
        case 2:
          attachmentListConStyle = {
            gridTemplateColumns: "repeat(2,1fr)",
          };
          mainContainerStyle = {
            maxWidth: "50%",
            width: "100%",
          };
          break;
        default:
          attachmentListConStyle = {
            gridTemplateColumns: "repeat(2,1fr)",
          };
          mainContainerStyle = {
            width: "100%",
          };
          break;
      }
      attachmentListConStyle["position"] = "relative";
    }

    if (showFlatMessage) {
      mainContainerStyle = {
        maxWidth: "unset",
        flex: 1,
      };
    }

    const messageToDisplay = getMessage();

    if (isRightPaneChat) {
      attachmentListConStyle = {
        ...attachmentListConStyle,
        gridTemplateColumns: "repeat(1,1fr)",
        gap: "8px",
        marginTop: "14px",
        ...attachmentListContainerStyle,
      };
    }

    attachmentListConStyle = {
      ...attachmentListConStyle,
      ...attachmentListContainerStyleV2,
    };

    const remarkTextMsgComponent = () => {
      return (
        <>
          <div className={classes.remarkIconContainer}>
            <PinFilled size={"xxx-small"} disabled={true} />
          </div>
          <div className={classes.remarkText}>
            {t("classRoom:pinned_as_remark")}
          </div>
        </>
      );
    };

    const pinIconComponent = () => {
      return (
        <LinkWithTooltip
          tooltip={pinToolTipText}
          placement={"top"}
          tooltipContainerStyle={pinToolTipStyle}
        >
          <div
            className={pinIconContainer}
            onClick={pinMessageAsRemark}
            style={pinIconStyle}
            role="button"
            tabIndex={0}
            aria-label={pinToolTipText}
            onKeyDown={handlePinKeyDown}
          >
            <PinOutlined
              size={"xxx-small"}
              variant={"subtle"}
              disabled={pinIconDisable}
            />
          </div>
        </LinkWithTooltip>
      );
    };

    const pinIconDisable = _.size(_.get(pinMessages, "edges", [])) > 0;
    const disablePinToggle = pinIconDisable && !isRemark;

    const pinMessageAsRemark = () => {
      if (!isPinMessageLoading && !disablePinToggle) {
        togglePinMessage();
      }
    };

    const pinIconStyle = { cursor: pinIconDisable ? "not-allowed" : "pointer" };

    const pinFilledIconStyle = { cursor: "pointer" };

    const pinToolTipText = pinIconDisable
      ? t("classRoom:pin_remark_alert")
      : t("classRoom:pin_remark");

    const pinToolTipStyle = { maxWidth: 284, textAlign: "center" };
    const handlePinKeyDown = e => {
      onEnterOrSpaceKeyDown(e, pinMessageAsRemark);
    };
    const itemId = _.get(item, "id", "");

    const showPinIcon =
      showRemarkOption &&
      isOwnChat &&
      userType === "staff" &&
      !isPinPopup &&
      !_.includes(itemId, "NEW");

    const messageWrapperClasses = classNames({
      [classes.messageWrapper]: true,
      [classes.alert]: isAlertTypeMessage,
    });

    const hideSenderNameForAlertMessage =
      userType === "staff" && isAlertTypeMessage;

    return (
      <div className={mainContainerClasses} style={mainContainerStyle}>
        <div className={containerClass} style={flatMsgContainerStyle}>
          {showPinIcon && !isRemark && !showFlatMessage && !isDeleted && (
            <div className={showPinContainer}>{pinIconComponent()}</div>
          )}
          {!isDeleted &&
            !horizontalMessageCardV2 &&
            !hideSenderNameForAlertMessage && (
              <div className={classes.senderInfo}>
                {(showSenderInfo || (showPinIcon && isRemark)) && (
                  <div className={classes.senderName} style={senderNameStyle}>
                    {getFormattedUserName({ user: createdBy })}
                  </div>
                )}
                {showPinIcon &&
                  !isRemark &&
                  showFlatMessage &&
                  pinIconComponent()}
                {showPinIcon && isRemark && (
                  <div
                    className={pinIconFilledContainer}
                    onClick={pinMessageAsRemark}
                    style={pinFilledIconStyle}
                    role="button"
                    tabIndex={0}
                    aria-label={t("classRoom:un_pin_remark")}
                    onKeyDown={handlePinKeyDown}
                  >
                    <LinkWithTooltip
                      tooltip={t("classRoom:un_pin_remark")}
                      placement={"top"}
                      tooltipContainerStyle={pinToolTipStyle}
                    >
                      <PinFilled size={"xxx-small"} variant={"link"} />
                    </LinkWithTooltip>
                  </div>
                )}
              </div>
            )}
          <div className={classes.messageSubCon} style={messageSubConStyle}>
            {messageToDisplay && !isDeleted && (
              <div
                style={messageWrapperStyle}
                className={messageWrapperClasses}
                dangerouslySetInnerHTML={{
                  __html: getStyleStrippedText({
                    text: getCommentDisplayContent(messageToDisplay),
                    type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                  }),
                }}
              ></div>
            )}
            {isDeleted && (
              <div className={classes.deletedMsg}>
                <DeleteOutlined
                  className={classes.deletedMsg}
                  variant={"subtle"}
                />
                {t("common:message_was_deleted")}
              </div>
            )}
            {_.get(item, "voiceInstruction") && !isDeleted && (
              <AudioRecorderSelector
                mode="view"
                label={"Audio Instructions"}
                value={_.get(item, "voiceInstruction")}
                name={"voiceInstruction"}
              />
            )}
            {attachments.length > 0 && !isDeleted && (
              <AttachmentList
                value={attachments}
                itemStyles={{
                  backgroundPosition: "center",
                }}
                cardType={attachmentCardType}
                mode={"view"}
                showCreateDropdown={false}
                clubbingCards={4}
                showSingleCardTheme={true}
                attachmentContainerStyle={attachmentListConStyle}
                containerStyle={{
                  gridGap: "40px 24px",
                }}
                horizontalCardStyle={horizontalCardStyle}
                isRightPaneChat={isRightPaneChat}
              />
            )}
          </div>
        </div>
        {!isDeleted && (
          <div className={messageFooterClasses}>
            {showMessageTime && (
              <div className={messageBottomTextContainer}>
                {showRemarkText && userType === "student" ? (
                  <div className={classes.reamarkMsgContainer}>
                    {remarkTextMsgComponent()}
                    <div className={classes.remarkTextDot}></div>
                    <Time time={createdAt} />
                  </div>
                ) : (
                  <Time time={createdAt} />
                )}
              </div>
            )}
            {showMessageTime && showIsRead && (
              <span className={classes.separator}></span>
            )}
            {showIsRead && (
              <div className={classes.read}>
                <div>
                  <DoubleTick fill={isRead ? undefined : colors.neutral400} />
                </div>
                {isRead && <div>{t("common:read")}</div>}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

MessageAttachmentCard.defaultProps = {
  showCustomAttachmentList: false,
  isRemark: false,
  messageSubConStyle: {},
  attachmentListContainerStyle: {},
  flatMsgContainerStyle: {},
};

MessageAttachmentCard.displayName = "MessageAttachmentCard";

export default compose(I18nHOC({ resource: ["common", "classRoom"] }))(
  MessageAttachmentCard
);
